import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { DotsHorizontalIcon } from "@heroicons/react/outline";
import {
  MarkButton,
  HeadingButton,
  ListButton,
  ImageButton,
  MessageBoxButton,
  TableControls,
  UndoButton,
  RedoButton,
} from "./toolbar/index";
import { ProjectDataButton } from "./toolbar/ProjectDataButton";
import { Tooltip } from "../../Tooltip";

export {
  MarkButton,
  HeadingButton,
  ListButton,
  ImageButton,
  MessageBoxButton,
  TableControls,
} from "./toolbar/index";

export { toggleMark, isMarkActive } from "./toolbar/utils";

interface ToolbarButton {
  component: React.ReactElement;
  tooltip: string;
}

const toolbarButtons: ToolbarButton[][] = [
  [
    {
      component: <UndoButton />,
      tooltip: "Undo",
    },
    {
      component: <RedoButton />,
      tooltip: "Redo",
    },
  ],
  [
    {
      component: <MarkButton format="bold" icon="B" />,
      tooltip: "Bold",
    },
    {
      component: <MarkButton format="italic" icon="I" />,
      tooltip: "Italic",
    },
    {
      component: <MarkButton format="underline" icon="U" />,
      tooltip: "Underline",
    },
    {
      component: <MarkButton format="highlight" icon="H" />,
      tooltip: "Highlight",
    },
  ],
  [
    {
      component: <HeadingButton level={1} icon="H1" />,
      tooltip: "Heading 1",
    },
    {
      component: <HeadingButton level={2} icon="H2" />,
      tooltip: "Heading 2",
    },
    {
      component: <HeadingButton level={3} icon="H3" />,
      tooltip: "Heading 3",
    },
    {
      component: <HeadingButton level={4} icon="H4" />,
      tooltip: "Heading 4",
    },
  ],
  [
    {
      component: <ListButton format="bulleted" icon="bullet" />,
      tooltip: "Bullet List",
    },
    {
      component: <ListButton format="numbered" icon="list" />,
      tooltip: "Numbered List",
    },
  ],
  [
    {
      component: <TableControls />,
      tooltip: "Table Controls",
    },
    {
      component: <ImageButton />,
      tooltip: "Insert Image",
    },
  ],
  [
    {
      component: <MessageBoxButton format="info" />,
      tooltip: "Info Message",
    },
    {
      component: <MessageBoxButton format="attention" />,
      tooltip: "Attention Message",
    },
    {
      component: <MessageBoxButton format="warning" />,
      tooltip: "Warning Message",
    },
    {
      component: <MessageBoxButton format="danger" />,
      tooltip: "Danger Message",
    },
  ],
  [
    {
      component: <ProjectDataButton />,
      tooltip: "Project Data",
    },
  ],
];

// Central formatting actions stay visible at all widths; the rest collapse into
// the "…" overflow menu when the toolbar runs out of room.
const PRIMARY_GROUPS = toolbarButtons.slice(0, 4);
const SECONDARY_GROUPS = toolbarButtons.slice(4);

const ToolbarGroup: React.FC<{ group: ToolbarButton[] }> = ({ group }) => (
  <div className="flex items-center gap-1 border-r border-gray-200 pr-2 mr-2 last:border-r-0 last:pr-0 last:mr-0">
    {group.map((button) => (
      <Tooltip key={button.tooltip} text={button.tooltip}>
        {button.component}
      </Tooltip>
    ))}
  </div>
);

export const Toolbar = () => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const primaryRef = useRef<HTMLDivElement>(null);
  const secondaryMirrorRef = useRef<HTMLDivElement>(null);
  const moreRef = useRef<HTMLDivElement>(null);
  const [collapsed, setCollapsed] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);

  // Collapse the secondary groups into "…" once primary + secondary no longer
  // fit the toolbar width. The hidden mirror gives a stable full-width reading
  // (independent of the current collapsed state) so the decision can't flicker.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const measure = () => {
      const available = container.clientWidth;
      const primaryWidth = primaryRef.current?.scrollWidth ?? 0;
      const secondaryWidth = secondaryMirrorRef.current?.scrollWidth ?? 0;
      setCollapsed(primaryWidth + secondaryWidth > available);
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  // Close the overflow menu on outside click.
  useEffect(() => {
    if (!moreOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (moreRef.current && !moreRef.current.contains(event.target as Node)) {
        setMoreOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [moreOpen]);

  return (
    <div
      ref={containerRef}
      className="relative p-2 flex flex-wrap items-center gap-y-2"
    >
      <div ref={primaryRef} className="flex items-center">
        {PRIMARY_GROUPS.map((group, idx) => (
          <ToolbarGroup key={idx} group={group} />
        ))}
      </div>

      {!collapsed && (
        <div className="flex items-center">
          {SECONDARY_GROUPS.map((group, idx) => (
            <ToolbarGroup key={idx} group={group} />
          ))}
        </div>
      )}

      {collapsed && (
        <div className="relative" ref={moreRef}>
          <button
            type="button"
            title={t("manual.toolbar.more")}
            aria-label={t("manual.toolbar.more")}
            className="h-9 w-9 inline-flex items-center justify-center text-gray-600 hover:bg-gray-100 rounded"
            onMouseDown={(event) => {
              event.preventDefault();
              setMoreOpen((open) => !open);
            }}
          >
            <DotsHorizontalIcon className="h-5 w-5" />
          </button>
          {moreOpen && (
            <div className="absolute right-0 top-full mt-1 z-50 bg-white border border-gray-200 rounded-lg shadow-lg p-1 flex items-center">
              {SECONDARY_GROUPS.map((group, idx) => (
                <ToolbarGroup key={idx} group={group} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Hidden mirror: measures the full secondary width regardless of state. */}
      <div
        ref={secondaryMirrorRef}
        aria-hidden
        className="absolute -left-[9999px] top-0 invisible pointer-events-none flex items-center"
      >
        {SECONDARY_GROUPS.map((group, idx) => (
          <ToolbarGroup key={idx} group={group} />
        ))}
      </div>
    </div>
  );
};
